import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { theme } from '../../../theme';
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  LinearProgress,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { LoadingButton } from '@mui/lab';
import AdmitCardFormModal from "../../../components/Modals/AdmitCardFormModal";
import { useAdmitCards } from "../../../hooks/useAdmitCards";
import { useStudents, getStudentPhotoBlob } from "../../../hooks/useStudents";
import { useAuth, AuthContext } from "../../../contexts/AuthContext";
import { useSessionClass, SessionClassProvider } from "contexts/SessionClassContext";
import { useError } from "../../../contexts/ErrorContext";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AdmitCardTemplate from "../../../components/templates/AdmitCardTemplate";
import { useTranslation } from "react-i18next";
import EnhancedTable from "../../../components/common/EnhancedTable";
import ActionButtons from "../../../components/common/ActionButtons";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useSchools } from "../../../hooks/useSchools";
import { useSessions } from "hooks/useSession";
import { useClasses } from "hooks/useClasses";
import axios from 'axios';
import { useCurrentStudent } from "../../../hooks/useCurrentStudent";

// ------------------------------
// QueryClient for bulk generation
// ------------------------------
const bulkQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// ------------------------------
// Get real user data from localStorage
// ------------------------------
const getRealAuthValue = () => {
  try {
    const token = localStorage.getItem('token');
    const userString = localStorage.getItem('user');
    const tenantSchema = localStorage.getItem('tenantSchema');

    if (token && userString) {
      const userData = JSON.parse(userString);

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (tenantSchema) {
        axios.defaults.headers.common['X-Tenant-ID'] = tenantSchema;
      }

      return {
        user: userData,
        isAuthenticated: true,
        isLoading: false,
        wasAutoLoggedOut: false,
        login: async () => userData,
        register: async () => userData,
        forgotPassword: async () => '',
        verifyOtp: async () => '',
        resetPassword: async () => '',
        logout: () => {},
        clearAutoLogoutFlag: () => {},
        isAdmin: () => userData.roles?.includes('ADMIN') || false,
        isSchoolAdmin: () => userData.roles?.includes('SCHOOL_ADMIN') || false,
        isTeacher: () => userData.roles?.includes('TEACHER') || false,
        isStudent: () => userData.roles?.includes('STUDENT') || false
      };
    }
  } catch (error) {
  }

  const fallbackUser = {
    id: 1,
    username: 'admin',
    email: 'admin@example.com',
    fullName: 'Administrator',
    schoolId: 1,
    roles: ['ADMIN']
  };

  return {
    user: fallbackUser,
    isAuthenticated: true,
    isLoading: false,
    wasAutoLoggedOut: false,
    login: async () => fallbackUser,
    register: async () => fallbackUser,
    forgotPassword: async () => '',
    verifyOtp: async () => '',
    resetPassword: async () => '',
    logout: () => {},
    clearAutoLogoutFlag: () => {},
    isAdmin: () => true,
    isSchoolAdmin: () => false,
    isTeacher: () => false,
    isStudent: () => false
  };
};


// ------------------------------
// Template wrapper with providers
// ------------------------------
// Custom AdmitCardTemplate for bulk download that handles images differently
const BulkAdmitCardTemplate: React.FC<{ data: any; hidePrintButton?: boolean }> = ({ data, hidePrintButton }) => {
  const authValue = getRealAuthValue();

  // Create a modified data object that includes the pre-fetched photo and logo
  const modifiedData = {
    ...data,
    student: {
      ...data.student,
      // Use pre-fetched photo URL if available
      photoUrl: data.student.preFetchedPhotoUrl || null
    },
    school: {
      ...data.school,
      // Ensure school logo is properly set
      schoolLogo: data.school.schoolLogo || data.school?.schoolLogo
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={bulkQueryClient}>
        <AuthContext.Provider value={authValue}>
          <SessionClassProvider>
            <AdmitCardTemplate data={modifiedData} hidePrintButton={hidePrintButton} />
          </SessionClassProvider>
        </AuthContext.Provider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

const AdmitCardTemplateWithContext: React.FC<{ data: any; hidePrintButton?: boolean }> = ({ data, hidePrintButton }) => {
  const authValue = getRealAuthValue();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={bulkQueryClient}>
        <AuthContext.Provider value={authValue}>
          <SessionClassProvider>
            <AdmitCardTemplate data={data} hidePrintButton={hidePrintButton} />
          </SessionClassProvider>
        </AuthContext.Provider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

// ------------------------------
// Utilities for reliable rendering
// ------------------------------
const waitNextFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => setTimeout(resolve, 0));
  });

const waitForImages = async (el: HTMLElement) => {
  const imgs = Array.from(el.querySelectorAll("img"));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((res) => {
          if (img.complete && img.naturalWidth) return res();
          img.addEventListener("load", () => res(), { once: true });
          img.addEventListener("error", () => res(), { once: true });
        })
    )
  );
};

// Preload logo image to ensure it's cached
const preloadLogo = async (logoUrl: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      resolve();
    };
    img.onerror = () => {
      resolve(); // Don't fail the entire process if logo fails
    };
    img.src = logoUrl;
  });
};

const waitForFonts = async () => {
  try {
    // @ts-ignore
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {
    /* ignore */
  }
};

const hasRenderableContent = (node: HTMLElement) => {
  const txt = node.textContent?.trim() ?? "";
  return txt.length > 20 || node.children.length > 0;
};

const A4_MM = { w: 210, h: 297 };

const renderCardOnce = async (
  data: any,
  TemplateWithProviders: React.FC<any>
): Promise<HTMLDivElement> => {
  const host = document.createElement("div");
  Object.assign(host.style, {
    position: "absolute",
    left: "-10000px",
    top: "0",
    width: "210mm",
    height: "297mm",
    background: "#fff",
    margin: "0",
    padding: "0",
    boxSizing: "border-box",
    overflow: "hidden",
  } as CSSStyleDeclaration);
  document.body.appendChild(host);

  const root = createRoot(host);
  root.render(
    React.createElement(TemplateWithProviders, {
      data,
      hidePrintButton: true,
    })
  );

  // Let React + MUI paint, then wait fonts & images
  await waitNextFrame();
  await waitNextFrame();
  await waitForFonts();
  await waitForImages(host);
  await waitNextFrame();

  // Additional wait for logo specifically
  const logoImg = host.querySelector('img[alt*="Logo"]') as HTMLImageElement;
  if (logoImg) {
    await new Promise<void>((resolve) => {
      if (logoImg.complete && logoImg.naturalWidth) {
        resolve();
      } else {
        logoImg.addEventListener('load', () => resolve(), { once: true });
        logoImg.addEventListener('error', () => resolve(), { once: true });
        // Timeout after 5 seconds
        setTimeout(() => resolve(), 5000);
      }
    });
  }

  // Fallback retries if template injects async content
  if (!hasRenderableContent(host)) {
    for (let i = 0; i < 4; i++) {
      await waitNextFrame();
      await waitForImages(host);
      if (hasRenderableContent(host)) break;
    }
  }

  return host;
};

// Just remove the node (React 18 createRoot is not stored globally here)
const removeHost = (host: HTMLDivElement) => {
  host.remove();
};

const AdmitCardManagement: React.FC = () => {
  const { selectedSession, selectedClass, setSelectedSession, setSelectedClass } = useSessionClass();

  // Add query client for cache invalidation
  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const { showError, showSuccess } = useError();
  const { user } = useAuth();
  const isTeacher = user?.roles?.includes("TEACHER");
  const isSchoolAdmin = user?.roles?.includes("SCHOOL_ADMIN");
  const isStudent = user?.roles?.includes("STUDENT");

  const {
    admitCardsQuery,
    createAdmitCardMutation,
    updateAdmitCardMutation,
    deleteAdmitCardMutation,
    getAdmitCardByClassIdMutation,
  } = useAdmitCards(isStudent); // Disable general query for students
  const userEmail = user?.email;
  const sessionId = selectedSession?.id;

  // For students, use the current student hook to get student data from login (uses mobile number API)
  // For non-students, use the students hook (but only if needed)
  const { studentData: currentStudentData, studentLoading: currentStudentLoading } = useCurrentStudent();
  const { studentsQuery, useStudentsByClassName, useStudentsByClassId, getStudentByStudentCode, useStudentByMobileNo } =
    useStudents(false, undefined, !isStudent); // Disable for students

  // For students: Explicitly fetch student data using mobile number API to get classId and sessionId
  // Use selectedSession and selectedClass from context (which are set by Layout based on student data)
  const studentMobile = isStudent ? (user?.email || "") : "";
  const isValidMobile = studentMobile && !studentMobile.includes('@') && /^\d{10}$/.test(studentMobile.replace(/\D/g, ''));

  // Fetch student data using mobile number API with sessionId and classId from context
  const {
    data: studentDataByMobile,
    isLoading: loadingStudentByMobile
  } = useStudentByMobileNo(
    isStudent && isValidMobile ? studentMobile : undefined,
    isStudent && isValidMobile && selectedSession?.id ? selectedSession.id : undefined,
    isStudent && isValidMobile && selectedClass?.id ? selectedClass.id : undefined
  );

  // For students: Use studentDataByMobile (from explicit mobile API call) if available, otherwise use currentStudentData
  // Both come from the mobile number API, but studentDataByMobile uses explicit sessionId/classId from context
  const studentDataForAdmitCard = isStudent ? (studentDataByMobile || currentStudentData) : null;

  // Auto-select class and session for students based on their student object
  useEffect(() => {
    if (isStudent && studentDataForAdmitCard) {
      // Auto-select class and session if not already selected
      if (studentDataForAdmitCard.schoolClassId && !selectedClass) {
        // We need to fetch classes first, this will be handled in the component that has access to classes
      }
      if (studentDataForAdmitCard.sessionId && !selectedSession) {
        // We need to fetch sessions first, this will be handled in the component that has access to sessions
      }
    }
  }, [isStudent, studentDataForAdmitCard, selectedClass, selectedSession, setSelectedClass, setSelectedSession]);

  const [lastFetchedClassSession, setLastFetchedClassSession] = useState<{ classId: number; sessionId: number } | null>(null);

  // For students: Use classId and sessionId from mobile number API response to fetch admit cards
  // Call the API when component loads and student data is available
  useEffect(() => {
    if (isStudent) {
      // Prioritize studentDataByMobile (explicit API call), fall back to currentStudentData
      const studentData = studentDataByMobile || currentStudentData;

      if (studentData) {
        const currentClassId = studentData.schoolClassId;
        const currentSessionId = studentData.sessionId;

        // Call API if we have both classId and sessionId
        if (currentClassId && currentSessionId) {
          // Only fetch if class/session combination has changed or hasn't been fetched yet
          if (
            !lastFetchedClassSession ||
            lastFetchedClassSession.classId !== currentClassId ||
            lastFetchedClassSession.sessionId !== currentSessionId
          ) {
            setLastFetchedClassSession({ classId: currentClassId, sessionId: currentSessionId });
            // Call the admit card API: GET /admit-cards/class/{classId}/{sessionId}
            getAdmitCardByClassIdMutation.mutate({
              classId: currentClassId,
              sessionId: currentSessionId,
            });
          }
        } else {
        }
      } else {
      }
    }
  }, [isStudent, studentDataByMobile, currentStudentData, lastFetchedClassSession, getAdmitCardByClassIdMutation, selectedSession, selectedClass]);


  // For students, use studentDataForAdmitCard. For non-students, find from studentsQuery
  const currentStudent = isStudent ? studentDataForAdmitCard : (studentsQuery.data?.find(
    (student) => student.gmail === userEmail
  ));

  // Get all student IDs for the selected class (only for non-students)
  const { data: classStudents = [] } = useStudentsByClassId(
    !isStudent ? (selectedClass?.id || 0) : 0
  );

  const matchesSelectedSession = (student: any) => {
    if (!selectedSession) return true;

    const selectedSessionId = selectedSession.id;
    const selectedSessionName = selectedSession.name?.toString().toLowerCase();

    const studentSessionId =
      student.sessionId ??
      student.session_id ??
      student.session?.id ??
      student.session?.sessionId ??
      student.session?.session_id;

    const studentSessionName = (
      student.sessionName ??
      student.session?.name ??
      student.session?.sessionName
    )
      ?.toString()
      .toLowerCase();

    if (
      selectedSessionId !== undefined &&
      selectedSessionId !== null &&
      studentSessionId !== undefined &&
      studentSessionId !== null
    ) {
      const selectedIdNumber = Number(selectedSessionId);
      const studentIdNumber = Number(studentSessionId);

      if (!Number.isNaN(selectedIdNumber) && !Number.isNaN(studentIdNumber) && selectedIdNumber === studentIdNumber) {
        return true;
      }

      if (String(selectedSessionId) === String(studentSessionId)) {
        return true;
      }
    }

    if (selectedSessionName && studentSessionName) {
      return selectedSessionName === studentSessionName;
    }

    return false;
  };

  // Helper function to get student display name
  const getStudentDisplayName = (student: any): string => {
    if (student.studentName) {
      return student.studentName;
    } else if (student.firstName && student.lastName) {
      return `${student.firstName} ${student.lastName}`;
    } else if (student.firstName) {
      return student.firstName;
    } else if (student.lastName) {
      return student.lastName;
    } else {
      return "Unknown Student";
    }
  };

  const sessionScopedStudents = useMemo(() => {
    if (!classStudents || classStudents.length === 0) {
      return [];
    }

    return classStudents.filter((student: any) => {
      const matchesSession = matchesSelectedSession(student);
      const matchesClass = selectedClass
        ? Number(student.schoolClassId ?? student.classId) === Number(selectedClass.id)
        : true;
      return matchesSession && matchesClass;
    });
  }, [classStudents, selectedSession, selectedClass]);

  const activeSessionStudents = useMemo(
    () => sessionScopedStudents.filter((student: any) => student.status === true),
    [sessionScopedStudents]
  );

  const bulkAdmitVisibleStudents = useMemo(() => {
    return [...activeSessionStudents]
      .filter((s: any) => typeof s.id === "number")
      .sort((a: any, b: any) =>
        getStudentDisplayName(a).localeCompare(getStudentDisplayName(b))
      );
  }, [activeSessionStudents]);

  const getStudentName = useCallback(
    (id: number) => {
      const student = sessionScopedStudents.find((s) => s.id === id);
      return student ? getStudentDisplayName(student) : id;
    },
    [sessionScopedStudents]
  );

  // Helper function to parse student subjects
  const parseStudentSubjects = (subjectsData: any): string[] => {
    if (!subjectsData) return [];

    try {
      // If it's already an array, use it directly
      if (Array.isArray(subjectsData)) {
        return subjectsData.filter(Boolean).map(s => String(s).trim());
      }

      // If it's a string, split by comma
      if (typeof subjectsData === 'string') {
        return subjectsData.split(',').map(s => s.trim()).filter(Boolean);
      }

      return [];
    } catch (error) {
      return [];
    }
  };

  // Helper function to check if a subject matches student's subjects
  const doesSubjectMatch = (scheduleSubjectCode: string, scheduleSubjectName: string, studentSubjectsList: string[]): boolean => {
    if (!studentSubjectsList || studentSubjectsList.length === 0) return false;

    const scheduleCodeLower = scheduleSubjectCode.toLowerCase();
    const scheduleNameLower = scheduleSubjectName.toLowerCase();

    return studentSubjectsList.some(studentSubject => {
      const studentSubjectStr = String(studentSubject).trim().toLowerCase();

      // Match by subject code or subject name (case-insensitive)
      return scheduleCodeLower === studentSubjectStr ||
        scheduleNameLower === studentSubjectStr ||
        scheduleCodeLower.includes(studentSubjectStr) ||
        scheduleNameLower.includes(studentSubjectStr) ||
        studentSubjectStr.includes(scheduleCodeLower) ||
        studentSubjectStr.includes(scheduleNameLower);
    });
  };

  // Helper function to filter exam schedules based on student subjects
  const filterExamSchedulesForStudent = (examSchedules: any[], studentSubjectsList: string[]): any[] => {
    if (!isStudent || !studentSubjectsList || studentSubjectsList.length === 0) {
      return examSchedules || [];
    }

    return (examSchedules || []).filter((schedule: any) => {
      const scheduleSubjectCode = schedule.subjectCode ? String(schedule.subjectCode).trim() : '';
      const scheduleSubjectName = schedule.subjectName ? String(schedule.subjectName).trim() : '';
      return doesSubjectMatch(scheduleSubjectCode, scheduleSubjectName, studentSubjectsList);
    });
  };

  // Get student subjects for filtering - use studentDataForAdmitCard from mobile number API
  const studentSubjects = useMemo(() => {
    if (!isStudent || !studentDataForAdmitCard) return [];
    const subjectsData = studentDataForAdmitCard.subjects;
    if (!subjectsData) {
      return [];
    }
    return parseStudentSubjects(subjectsData);
  }, [isStudent, studentDataForAdmitCard]);

  // Filter admit cards for students - show all cards for their class/session
  // The API already filters by classId and sessionId, so we just need to ensure they match
  const admitCards = isStudent
    ? (() => {
      const allAdmitCards = getAdmitCardByClassIdMutation.data || [];
      const studentClassId = studentDataForAdmitCard?.schoolClassId;
      const studentSessionId = studentDataForAdmitCard?.sessionId;

      // Filter by class/session match first
      let filteredCards = allAdmitCards.filter((card: any) => {
        const classMatches = !studentClassId || card.classId === studentClassId;
        const sessionMatches = !studentSessionId || card.sessionId === studentSessionId;
        return classMatches && sessionMatches;
      });

      // If student has subjects, further filter by subject matching
      if (studentSubjects && studentSubjects.length > 0) {
        filteredCards = filteredCards.filter((card: any) => {
          // If card has exam schedules, check for subject match
          if (card.examSchedules && Array.isArray(card.examSchedules) && card.examSchedules.length > 0) {
            const hasMatchingSubject = card.examSchedules.some((schedule: any) => {
              const scheduleSubjectCode = schedule.subjectCode ? String(schedule.subjectCode).trim() : '';
              const scheduleSubjectName = schedule.subjectName ? String(schedule.subjectName).trim() : '';
              return doesSubjectMatch(scheduleSubjectCode, scheduleSubjectName, studentSubjects);
            });
            return hasMatchingSubject;
          }
          // If no exam schedules but student has subjects, still show the card (less strict)
          return true;
        });
      }

      return filteredCards;
    })()
    : (admitCardsQuery.data || []).filter((card) => {
      return (
        (!selectedSession || card.sessionId === selectedSession.id) &&
        (!selectedClass || card.classId === selectedClass.id)
      );
    });



  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentAdmitCard, setCurrentAdmitCard] = useState<any>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [admitCardToDelete, setAdmitCardToDelete] = useState<number | null>(
    null
  );

  // New state for view admit card dialog
  const [viewAdmitCardDialogOpen, setViewAdmitCardDialogOpen] = useState(false);
  const [admitCardType, setAdmitCardType] = useState("");
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [isSearching, setIsSearching] = useState(false);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewCard, setViewCard] = useState<any>(undefined);

  // New state for view admit card without student details
  const [viewAdmitCardOnlyOpen, setViewAdmitCardOnlyOpen] = useState(false);
  const [viewAdmitCardOnly, setViewAdmitCardOnly] = useState<any>(undefined);

  const [isGenerating, setIsGenerating] = useState(false);
  const admitCardRef = useRef<HTMLDivElement>(null);

  // Bulk download state
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [selectedExamType, setSelectedExamType] = useState<string>('');
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentBulkStudent, setCurrentBulkStudent] = useState('');
  const [bulkAdmitSelectedStudentIds, setBulkAdmitSelectedStudentIds] = useState<Set<number>>(
    new Set()
  );

  useEffect(() => {
    if (!bulkModalOpen) return;
    setBulkAdmitSelectedStudentIds(
      new Set(bulkAdmitVisibleStudents.map((s: any) => s.id as number))
    );
  }, [bulkModalOpen, selectedExamType, bulkAdmitVisibleStudents]);

  const bulkAdmitSelectAllChecked =
    bulkAdmitVisibleStudents.length > 0 &&
    bulkAdmitVisibleStudents.every((s: any) => bulkAdmitSelectedStudentIds.has(s.id));
  const bulkAdmitSelectAllIndeterminate =
    bulkAdmitSelectedStudentIds.size > 0 && !bulkAdmitSelectAllChecked;

  const fallbackSchoolId = user?.schoolId || 0;
  const schoolId = selectedClass?.schoolId || fallbackSchoolId;

  // Use useSchoolById instead of fetching all schools for all users
  const { useSchoolById } = useSchools(false, { disableSchoolAdmins: true });
  const { data: school } = useSchoolById(schoolId || undefined);

  const { sessionsQuery } = useSessions(schoolId);
  const { classesQuery } = useClasses(schoolId);

  const sessionOptions = sessionsQuery.data || [];
  const classOptions = classesQuery.data || [];

  const coerceId = useCallback((value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number") return Number.isNaN(value) ? null : value;
    if (typeof value === "string") {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }, []);

  const normalizedSessionId = coerceId(selectedSession?.id);
  const normalizedClassId = coerceId(selectedClass?.id);
  const hasSessionFilter = normalizedSessionId !== null;
  const hasClassFilter = normalizedClassId !== null;

  const filteredClasses = useMemo(() => {
    if (!classOptions || classOptions.length === 0) return [];
    if (!hasSessionFilter) return classOptions;

    return classOptions.filter((cls: any) => {
      const clsSessionId =
        coerceId(cls.sessionId) ??
        coerceId(cls.session?.id) ??
        coerceId((cls.sessionId as any)?.id);
      if (clsSessionId === null) return true;
      return clsSessionId === normalizedSessionId;
    });
  }, [classOptions, coerceId, hasSessionFilter, normalizedSessionId]);

  const availableStudentsForSelection = hasClassFilter ? activeSessionStudents : [];

  const sortedAvailableStudents = useMemo(() => {
    return [...availableStudentsForSelection].sort((a: any, b: any) => {
      const nameA = getStudentDisplayName(a).toLowerCase();
      const nameB = getStudentDisplayName(b).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [availableStudentsForSelection]);

  useEffect(() => {
    if (!viewAdmitCardDialogOpen) return;
    setSelectedStudent(null);
  }, [normalizedSessionId, normalizedClassId, viewAdmitCardDialogOpen]);

  // Debug school data
  useEffect(() => {
    if (school) {
    }
  }, [school]);

  // Client-side PDF generation functions
  const generatePdf = async (element: HTMLElement, fileName: string) => {
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(element, {
        useCORS: true,
        allowTaint: true,
        background: '#ffffff',
        logging: false,
        width: element.offsetWidth,
        height: element.offsetHeight,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.98);
      const pdf = new jsPDF({
        unit: 'in',
        format: 'a4',
        orientation: 'portrait',
        compress: true,
      });

      const imgWidth = 8.5;
      const pageHeight = 11;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(fileName);
      showSuccess(t("admit_card_downloaded_successfully") || "Admit card downloaded successfully!");
    } catch (error) {
      showError(t("failed_to_download_pdf") || "Failed to download PDF. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  // Exam type options
  const examTypeOptions = [
    { value: "QUARTERLY", label: "Quarterly" },
    { value: "HALF-YEARLY", label: "Half-Yearly" },
    { value: "ANNUAL", label: "Annual" },
    { value: "PRE-BOARD", label: "Pre-Board" },
    { value: "BOARD", label: "Board" },
  ];

  const handleOpenAddModal = () => {
    setIsEdit(false);
    setCurrentAdmitCard(undefined);
    setModalOpen(true);
  };

  const handleOpenEditModal = (admitCard: any) => {
    // Backend now sends boolean directly, no conversion needed
    setIsEdit(true);
    setCurrentAdmitCard(admitCard);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleSubmit = async (formData: any) => {
    // Ensure issued is boolean
    const processedData = {
      ...formData,
      issued: Boolean(formData.issued)
    };

    try {
      if (isEdit && currentAdmitCard?.id) {
        await updateAdmitCardMutation.mutateAsync({
          id: currentAdmitCard.id,
          data: processedData,
        });
        showSuccess(t("admit_card_updated_successfully") || "Admit card updated successfully!");
      } else {
        await createAdmitCardMutation.mutateAsync(processedData);
        showSuccess(t("admit_card_created_successfully") || "Admit card created successfully!");
      }
      handleCloseModal();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || t("error_processing_admit_card") || "Error processing admit card";
      showError(errorMessage);
    }
  };

  const handleOpenDeleteDialog = (id: number) => {
    setAdmitCardToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setAdmitCardToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (admitCardToDelete !== null) {
      deleteAdmitCardMutation.mutate(admitCardToDelete, {
        onSuccess: () => {
          showSuccess(t("admit_card_deleted_successfully") || "Admit card deleted successfully!");
          handleCloseDeleteDialog();
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || error?.message || t("error_deleting_admit_card") || "Error deleting admit card";
          showError(errorMessage);
        },
      });
    }
  };

  const handleOpenViewModal = async (card: any) => {
    let student = sessionScopedStudents.find((s) => s.id === card.studentId);
    let session = undefined;
    if (!sessionsQuery.isLoading && student && sessionsQuery.data) {
      const currentStudent = student; // Store in const for type narrowing
      const studentSessionId = currentStudent.sessionId;
      if (studentSessionId !== undefined && studentSessionId !== null) {
        session = sessionsQuery.data.find(
          (ses) => Number(ses.id) === Number(studentSessionId)
        );
      }
    }

    // For students viewing their own admit card, use their student data from mobile number API
    if (isStudent && studentDataForAdmitCard) {
      student = studentDataForAdmitCard;
    }

    // Fetch student photo if student has an ID
    let photoUrl = null;
    if (student?.id) {
      try {
        const photoBlob = await getStudentPhotoBlob(student.id);
        if (photoBlob) {
          photoUrl = URL.createObjectURL(photoBlob);
        }
      } catch (error) {
      }
    }

    // Prepare student data with all required fields and photo
    const studentDataForCard = student ? {
      ...student,
      enrolmentNo: student.enrolmentNo || (student as any).enrolmentNumber || '',
      studentName: student.studentName || `${(student as any).firstName || ''} ${(student as any).lastName || ''}`.trim() || 'Unknown Student',
      fatherName: student.fatherName || (student as any).father_name || '',
      motherName: student.motherName || (student as any).mother_name || '',
      rollNumber: student.rollNumber || (student as any).rollNo || '',
      dateOfBirth: student.dateOfBirth || '',
      gender: student.gender || '',
      className: student.className || selectedClass?.name || '',
      section: student.section || '',
      photoUrl: photoUrl || (student as any).photoUrl || (student as any).studentPhoto || null,
    } : undefined;

    // For students, filter exam schedules to only show their subjects
    let filteredCard = { ...card };
    if (isStudent && studentSubjects && studentSubjects.length > 0 && card.examSchedules) {
      filteredCard = {
        ...card,
        examSchedules: filterExamSchedulesForStudent(card.examSchedules, studentSubjects)
      };
    }

    setViewCard({ ...filteredCard, student: studentDataForCard, school, session });
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    // Cleanup photo URL if it was created from blob
    if (viewCard?.student?.photoUrl && viewCard.student.photoUrl.startsWith('blob:')) {
      URL.revokeObjectURL(viewCard.student.photoUrl);
    }
    setViewModalOpen(false);
    setViewCard(undefined);
  };

  // New handlers for view admit card only
  const handleOpenViewAdmitCardOnly = (card: any) => {
    // For students, filter exam schedules to only show their subjects
    let filteredCard = { ...card };
    if (isStudent && studentSubjects && studentSubjects.length > 0 && card.examSchedules) {
      filteredCard = {
        ...card,
        examSchedules: filterExamSchedulesForStudent(card.examSchedules, studentSubjects)
      };
    }
    setViewAdmitCardOnly(filteredCard);
    setViewAdmitCardOnlyOpen(true);
  };

  const handleCloseViewAdmitCardOnly = () => {
    setViewAdmitCardOnlyOpen(false);
    setViewAdmitCardOnly(undefined);
  };

  // New functions for view admit card dialog
  const handleOpenViewAdmitCardDialog = () => {
    setViewAdmitCardDialogOpen(true);
    setAdmitCardType("");
    setSelectedStudent(null);
  };

  const handleCloseViewAdmitCardDialog = () => {
    setViewAdmitCardDialogOpen(false);
    setAdmitCardType("");
    setSelectedStudent(null);
  };

  const handleSessionFilterChange = useCallback(
    (event: any) => {
      const value = event.target.value;
      if (value === "") {
        setSelectedSession(null);
        setSelectedClass(null);
        setSelectedStudent(null);
        return;
      }
      const numericValue = Number(value);
      const sessionObj = sessionOptions.find(
        (session: any) => Number(session.id) === numericValue
      );
      setSelectedSession(sessionObj || null);
      setSelectedClass(null);
      setSelectedStudent(null);
    },
    [sessionOptions, setSelectedSession, setSelectedClass]
  );

  const handleClassFilterChange = useCallback(
    (event: any) => {
      const value = event.target.value;
      if (value === "") {
        setSelectedClass(null);
        setSelectedStudent(null);
        return;
      }
      const numericValue = Number(value);
      const classObj = filteredClasses.find(
        (cls: any) => Number(cls.id) === numericValue
      );
      setSelectedClass(classObj || null);
      setSelectedStudent(null);
    },
    [filteredClasses, setSelectedClass]
  );

  const handleStudentSelect = useCallback(
    (event: any) => {
      const value = event.target.value;
      if (value === "") {
        setSelectedStudent(null);
        return;
      }
      const numericValue = Number(value);
      const student = sortedAvailableStudents.find(
        (s: any) => Number(s.id) === numericValue
      );
      setSelectedStudent(student || null);
    },
    [sortedAvailableStudents]
  );

  const handleSearchAdmitCard = async (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }

    if (!selectedStudent || !admitCardType.trim()) {
      showError(t("please_select_student_and_admit_card_type"));
      return;
    }

    setIsSearching(true);

    try {
      const student = selectedStudent;

      if (coerceId(student.sessionId) !== normalizedSessionId) {
        showError(t("student_roll_number_not_found_session_admit"));
        return;
      }

      const admitCard = admitCards.find(
        (card) =>
          card.examType === admitCardType &&
          card.classId === selectedClass?.id &&
          card.sessionId === selectedSession?.id
      );

      if (!admitCard) {
        showError(
          t("admit_card_not_found_exam_type_class_session", {
            examType: admitCardType,
            className: selectedClass?.name,
            sessionName: selectedSession?.name,
          })
        );
        return;
      }

      const isIssued = admitCard.issued;

      if (!isIssued) {
        showError(
          t("admit_card_not_issued_yet_exam_type", {
            examType: admitCardType,
          })
        );
        return;
      }

      // For students viewing their own admit card, use their student data from mobile number API
      let studentDataForCard = student;
      if (isStudent && studentDataForAdmitCard) {
        studentDataForCard = studentDataForAdmitCard;
      }

      // Fetch student photo if student has an ID
      let photoUrl = null;
      if (studentDataForCard?.id) {
        try {
          const photoBlob = await getStudentPhotoBlob(studentDataForCard.id);
          if (photoBlob) {
            photoUrl = URL.createObjectURL(photoBlob);
          }
        } catch (error) {
        }
      }

      // Prepare student data with all required fields and photo
      const finalStudentData = studentDataForCard ? {
        ...studentDataForCard,
        enrolmentNo: studentDataForCard.enrolmentNo || (studentDataForCard as any).enrolmentNumber || '',
        studentName: studentDataForCard.studentName || `${(studentDataForCard as any).firstName || ''} ${(studentDataForCard as any).lastName || ''}`.trim() || 'Unknown Student',
        fatherName: studentDataForCard.fatherName || (studentDataForCard as any).father_name || '',
        motherName: studentDataForCard.motherName || (studentDataForCard as any).mother_name || '',
        rollNumber: studentDataForCard.rollNumber || (studentDataForCard as any).rollNo || '',
        dateOfBirth: studentDataForCard.dateOfBirth || '',
        gender: studentDataForCard.gender || '',
        className: studentDataForCard.className || selectedClass?.name || '',
        section: studentDataForCard.section || '',
        photoUrl: photoUrl || (studentDataForCard as any).photoUrl || (studentDataForCard as any).studentPhoto || null,
      } : undefined;

      // For students, filter exam schedules to only show their subjects
      let filteredAdmitCard = { ...admitCard };
      if (isStudent && studentSubjects && studentSubjects.length > 0 && admitCard.examSchedules) {
        filteredAdmitCard = {
          ...admitCard,
          examSchedules: filterExamSchedulesForStudent(admitCard.examSchedules, studentSubjects)
        };
      }

      setViewCard({
        ...filteredAdmitCard,
        student: finalStudentData,
        school,
        session: selectedSession,
      });
      setViewModalOpen(true);
    } catch (error) {
      showError(t("error_searching_admit_card"));
    } finally {
      setIsSearching(false);
    }
  };

  // Bulk download handlers
  const handleOpenBulkModal = () => {
    setBulkModalOpen(true);
    setSelectedExamType('');
  };

  const handleCloseBulkModal = () => {
    if (isBulkGenerating) return;
    setBulkModalOpen(false);
    setSelectedExamType('');
    setProgress(0);
    setCurrentBulkStudent('');
    setBulkAdmitSelectedStudentIds(new Set());
  };

  // Bulk PDF generation function
  const handleBulkDownload = async () => {
    setProgress(0);
    setCurrentBulkStudent('');

    if (!selectedClass || !selectedSession || !selectedExamType) {
      showError(t('please_select_class_session_exam_type'));
      return;
    }

    // Find admit card for the selected exam type
    const admitCard = admitCards.find(
      (card) =>
        card.examType === selectedExamType &&
        card.classId === selectedClass.id &&
        card.sessionId === selectedSession.id
    );

    if (!admitCard) {
      showError(t("admit_card_not_found_exam_type_class_session_issued", {
        examType: selectedExamType,
        className: selectedClass.name,
        sessionName: selectedSession.name
      }));
      return;
    }

    // Check if the admit card is issued
    const isIssued = admitCard.issued;

    if (!isIssued) {
      showError(t("admit_card_not_issued_yet_issue_first", {
        examType: selectedExamType
      }));
      return;
    }

    if (bulkAdmitVisibleStudents.length === 0) {
      showError(t("no_active_students_found_class_session", {
        className: selectedClass.name,
        sessionName: selectedSession.name
      }));
      return;
    }

    const activeStudents = bulkAdmitVisibleStudents.filter((s: any) =>
      bulkAdmitSelectedStudentIds.has(s.id)
    );

    if (activeStudents.length === 0) {
      showError(
        (t("please_select_students_to_download") as string) ||
          "Please select at least one student to download."
      );
      return;
    }

    setIsBulkGenerating(true);

    // Pre-fetch student photos and school logo for better reliability
    const studentPhotos = new Map();
    let schoolLogoUrl = null;

    try {
      const className = selectedClass.name || 'Unknown';
      const sessionName = selectedSession.name || 'Unknown';
      const filename = `bulk-admit-cards-${className}-${sessionName}-${selectedExamType}-${activeStudents.length}-students.pdf`;

      // Pre-load school logo with retry mechanism
      if (school?.schoolLogo) {
        try {
          await preloadLogo(school.schoolLogo);
          schoolLogoUrl = school.schoolLogo;
        } catch (error) {
          // Don't set schoolLogoUrl to null, let the template handle it
          schoolLogoUrl = school.schoolLogo;
        }
      } else {
      }

      // Pre-fetch student photos
      for (const student of activeStudents) {
        if (student.id) {
          try {
            const photoBlob = await getStudentPhotoBlob(student.id);
            if (photoBlob) {
              const photoUrl = URL.createObjectURL(photoBlob);
              studentPhotos.set(student.id, photoUrl);
            } else {
              // Student doesn't have a photo - this is normal
              studentPhotos.set(student.id, null);
            }
          } catch (error) {
            studentPhotos.set(student.id, null);
          }
        }
      }


      // Create the PDF (use mm to match our A4 sizing)
      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
        putOnlyUsedFonts: true,
      });

      let pageIndex = 0;

      for (let i = 0; i < activeStudents.length; i++) {
        const student = activeStudents[i];

        // Update current student being processed
        setCurrentBulkStudent(getStudentDisplayName(student));

        let completeStudentData = student;

        try {
          // Use studentCode to fetch complete student data
          if (student.studentCode) {
            completeStudentData = await getStudentByStudentCode(student.studentCode);
          }
        } catch (error) {
          completeStudentData = student;
        }

        // Prepare student data for the template - match the structure used in regular view
        const studentData = {
          ...admitCard,
          student: {
            ...completeStudentData, // Spread all student properties
            id: completeStudentData.id, // Ensure student ID is preserved
            studentId: completeStudentData.id, // Also set studentId for compatibility
            studentName: getStudentDisplayName(completeStudentData), // Ensure studentName is set
            rollNumber: completeStudentData.rollNumber || '',
            enrolmentNo: completeStudentData.enrolmentNo || '',
            fatherName: completeStudentData.fatherName || '',
            motherName: completeStudentData.motherName || '',
            dateOfBirth: completeStudentData.dateOfBirth || '',
            gender: completeStudentData.gender || '',
            className: selectedClass.name || '',
            section: completeStudentData.section || '',
            subjects: (completeStudentData as any).subjects || [],
            address: (completeStudentData as any).address || '',
            mobileNo: (completeStudentData as any).mobileNo || (completeStudentData as any).phone || '',
            gmail: completeStudentData.gmail || '',
            // Include pre-fetched photo URL for bulk download
            preFetchedPhotoUrl: studentPhotos.get(completeStudentData.id) || null
          },
          school: {
            ...school,
            // Include pre-loaded logo URL for better reliability
            schoolLogo: schoolLogoUrl || school?.schoolLogo,
          },
          session: selectedSession,
        };

        // Debug: Log student data being passed

        // 1) Render one page off-screen with providers
        const host = await renderCardOnce(studentData, BulkAdmitCardTemplate);

        // Debug: Check if content is rendered

        // Additional wait to ensure all content is fully rendered
        await new Promise(resolve => setTimeout(resolve, 500));

        // Wait for images to load before capturing with improved logic
        const images = host.querySelectorAll('img');
        if (images.length > 0) {
          await Promise.all(
            Array.from(images).map((img, index) => {
              return new Promise((resolve) => {
                const timeoutId = setTimeout(() => {
                  resolve(true);
                }, 8000);

                if (img.complete && img.naturalWidth > 0) {
                  clearTimeout(timeoutId);
                  resolve(true);
                } else {
                  img.onload = () => {
                    clearTimeout(timeoutId);
                    resolve(true);
                  };
                  img.onerror = () => {
                    clearTimeout(timeoutId);
                    resolve(true); // Resolve even on error to not block
                  };
                }
              });
            })
          );
        }

        // 2) Capture that page with improved image handling
        const canvas = await html2canvas(host, {
          useCORS: true,
          allowTaint: true,
          background: '#ffffff',
          logging: false, // Disable logging for cleaner output
          width: Math.max(host.scrollWidth, host.offsetWidth),
          height: Math.max(host.scrollHeight, host.offsetHeight),
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.98);

        // 3) Add to PDF as full A4 page
        const pageW = A4_MM.w;
        const pageH = A4_MM.h;

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, 0, pageW, pageH);
        pageIndex++;

        // 4) Cleanup DOM node
        removeHost(host);

        // Update progress after each card is processed
        setProgress(Math.round(((i + 1) / activeStudents.length) * 100));
      }

      pdf.save(filename);
      const successMsg = t("successfully_generated_bulk_admit_card_pdf", {
        count: activeStudents.length,
        className,
        sessionName,
        examType: selectedExamType
      });
      // Show success message in top right corner using snackbar
      showSuccess(successMsg);

      // Cleanup pre-fetched photo URLs
      studentPhotos.forEach((url: string) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    } catch (error: any) {
      const errorMessage = error?.message || t('unknown_error');
      showError(t("failed_generate_bulk_pdf_admit", { error: errorMessage }));

      // Cleanup pre-fetched photo URLs on error
      studentPhotos.forEach((url: string) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    } finally {
      setIsBulkGenerating(false);
      setProgress(0);
      setCurrentBulkStudent('');
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t("admit_card_management")}
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            {!isStudent && (
              <>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#3092e3",
                    "&:hover": { backgroundColor: "primary.main" },
                    minWidth: '180px'
                  }}
                  onClick={handleOpenBulkModal}
                >
                  {t("bulk_download_admit_cards")}
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#3092e3",
                    "&:hover": { backgroundColor: "primary.main" }
                  }}
                  onClick={handleOpenViewAdmitCardDialog}
                >
                  {t("view_admit_card")}
                </Button>
              </>
            )}
            {(isTeacher || isSchoolAdmin) && (
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#3092e3",
                  "&:hover": { backgroundColor: "primary.main" }
                }}
                onClick={handleOpenAddModal}
              >
                {t("add_admit_card")}
              </Button>
            )}
          </Box>
        </Box>
        {(isStudent ? (getAdmitCardByClassIdMutation.isLoading || loadingStudentByMobile || currentStudentLoading) : admitCardsQuery.isLoading) && (
          <CircularProgress sx={{ display: "block", margin: "20px auto" }} />
        )}
        {isStudent && (getAdmitCardByClassIdMutation.isError || (!studentDataForAdmitCard && !loadingStudentByMobile && !currentStudentLoading)) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String(getAdmitCardByClassIdMutation.error) || (!studentDataForAdmitCard ? t("student_data_not_found") || "Student data not found" : t("error_loading_admit_cards"))}
          </Alert>
        )}
        {!isStudent && admitCardsQuery.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {admitCardsQuery.error.message}
          </Alert>
        )}
        {isStudent && !getAdmitCardByClassIdMutation.isLoading && !loadingStudentByMobile && !currentStudentLoading && !getAdmitCardByClassIdMutation.isError && admitCards.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("no_admit_cards_found") || "No admit cards found for your subjects"}
          </Alert>
        )}

        {/* Enhanced Table */}
        <EnhancedTable
          columns={[
            { id: "examType", label: t("exam_type"), minWidth: 130 },
            { id: "venue", label: t("venue"), minWidth: 130 },
            {
              id: "issued",
              label: t("issued"),
              minWidth: 100,
              format: (value: any) => {
                if (value === null || value === undefined) return "";
                return String(value);
              }
            },
          ]}
          data={admitCards.map((row: any) => ({
            ...row,
            studentName: getStudentName(row.studentId),
            issued: row.issued,
          }))}
          searchFields={["examType", "venue", "studentName"]}
          filterFields={[]}
          cardProps={{
            elevation: 0,
            sx: {
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent'
            }
          }}
          actions={(row: any) => (
            <Box sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "center",
              width: "100%"
            }}>
              <ActionButtons
                onView={() => handleOpenViewModal(row)}
                onEdit={() => handleOpenEditModal(row)}
                onDelete={() => handleOpenDeleteDialog(row.id)}
                showView={isStudent}
                showEdit={!isStudent}
                showDelete={!isStudent}
                viewTooltip={t("view")}
                editTooltip={t("edit")}
                deleteTooltip={t("delete")}
              />
              {!isStudent && (
                <IconButton
                  onClick={() => handleOpenViewAdmitCardOnly(row)}
                  size="small"
                  sx={{
                    color: "#1976d2",
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.08)",
                    },
                  }}
                  title={t("view_admit_card")}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
        />
      </Paper>

      {(isTeacher || isSchoolAdmin) && (
        <AdmitCardFormModal
          open={modalOpen}
          handleClose={handleCloseModal}
          initialData={currentAdmitCard}
          onSubmit={handleSubmit}
          isEdit={isEdit}
        />
      )}

      {/* View Admit Card Dialog - Only show for non-students */}
      {!isStudent && (
        <Dialog
          open={viewAdmitCardDialogOpen}
          onClose={handleCloseViewAdmitCardDialog}
          maxWidth="xs"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              maxWidth: "400px",
              width: "90%",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              {t("view_admit_card")}
            </Typography>
            <IconButton onClick={handleCloseViewAdmitCardDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <form onSubmit={handleSearchAdmitCard}>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("select_student_from_dropdown")}
                </Typography>

                <FormControl
                  fullWidth
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f5f5",
                      borderRadius: 2,
                      "& fieldset": { border: "none" },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#666",
                      "&.Mui-focused": { color: "#0168EA" },
                    },
                  }}
                  disabled={sessionOptions.length === 0}
                >
                  <InputLabel>{t("session")}</InputLabel>
                  <Select
                    value={normalizedSessionId ?? ""}
                    label={t("session")}
                    onChange={handleSessionFilterChange}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>
                        {t("select_session_placeholder", {
                          defaultValue: "Select Session",
                        })}
                      </em>
                    </MenuItem>
                    {sessionOptions.map((session: any) => (
                      <MenuItem key={session.id} value={Number(session.id)}>
                        {session.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  fullWidth
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f5f5",
                      borderRadius: 2,
                      "& fieldset": { border: "none" },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#666",
                      "&.Mui-focused": { color: "#0168EA" },
                    },
                  }}
                  disabled={!hasSessionFilter || filteredClasses.length === 0}
                >
                  <InputLabel>{t("class")}</InputLabel>
                  <Select
                    value={normalizedClassId ?? ""}
                    label={t("class")}
                    onChange={handleClassFilterChange}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>
                        {t("select_class_placeholder", {
                          defaultValue: "Select Class",
                        })}
                      </em>
                    </MenuItem>
                    {filteredClasses.map((cls: any) => (
                      <MenuItem key={cls.id} value={Number(cls.id)}>
                        {cls.section ? `${cls.name} ${cls.section}` : cls.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  fullWidth
                  required
                  variant="outlined"
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f8f9fb",
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#0168EA",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#0168EA",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      "&.Mui-focused": {
                        color: "#0168EA",
                      },
                      "&.MuiInputLabel-shrink": {
                        color: "#0168EA",
                      },
                    },
                  }}
                  disabled={
                    !hasClassFilter || sortedAvailableStudents.length === 0
                  }
                >
                  <InputLabel id="view-admit-card-student-label">
                    {t("select_student")}
                  </InputLabel>
                  <Select
                    labelId="view-admit-card-student-label"
                    label={t("select_student")}
                    value={selectedStudent?.id ?? ""}
                    onChange={handleStudentSelect}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: "60vh",
                          "& .MuiMenuItem-root": {
                            py: 1.5,
                            minHeight: "48px",
                            fontSize: "0.95rem",
                          },
                        },
                      },
                    }}
                  >
                    {sortedAvailableStudents.map((student: any) => {
                      const studentName = getStudentDisplayName(student);
                      return (
                        <MenuItem
                          key={student.id}
                          value={student.id}
                          sx={{
                            py: 1.5,
                            minHeight: "48px",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            sx={{ width: "100%" }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                backgroundColor: "primary.main",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mr: 2,
                                fontSize: "0.9rem",
                                fontWeight: "bold",
                                flexShrink: 0,
                              }}
                            >
                              {studentName.charAt(0).toUpperCase()}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body1"
                                fontWeight="600"
                                sx={{
                                  fontSize: "0.95rem",
                                  lineHeight: 1.2,
                                  mb: 0.5,
                                }}
                              >
                                {studentName}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                {hasClassFilter && sortedAvailableStudents.length === 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t("no_students_found_school")}
                  </Alert>
                )}

                <FormControl
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f8f9fb",
                    },
                  }}
                >
                  <InputLabel>{t("admit_card_type")}</InputLabel>
                  <Select
                    label={t("admit_card_type")}
                    value={admitCardType}
                    onChange={(e) => setAdmitCardType(e.target.value)}
                    displayEmpty
                  >
                    {examTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={handleCloseViewAdmitCardDialog} variant="outlined">
                {t("cancel")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: "#3092e3",
                  "&:hover": { backgroundColor: "primary.main" },
                }}
                disabled={
                  !hasSessionFilter ||
                  !hasClassFilter ||
                  !selectedStudent ||
                  !admitCardType ||
                  isSearching
                }
              >
                {isSearching ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    {t("loading")}...
                  </>
                ) : (
                  t("view_admit_card")
                )}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t("confirm_delete")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("confirm_delete_admit_card")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} sx={{ backgroundColor: "#3092e3", color: "white !important"}}>
            {t("cancel")}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth={false}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: '900px',
            width: '90vw',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t("admit_card_preview")}
          </Typography>
          <IconButton onClick={handleCloseViewModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div ref={admitCardRef}>
            {viewCard && (
              <AdmitCardTemplate
                data={viewCard}
                hidePrintButton={true}
                hideStudentDetails={false}
                showAllSubjects={false}
                hideRollNumberAndPhoto={false}
              />
            )}
          </div>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#3092e3",
                "&:hover": { backgroundColor: "primary.main" }
              }}
              disabled={isGenerating}
              onClick={() => {
                if (admitCardRef.current) {
                  generatePdf(admitCardRef.current, `admit-card${viewCard.student.enrolmentNo}.pdf`);
                }
              }}
            >
              {isGenerating ? "Generating PDF..." : "Generate PDF"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* View Admit Card Only Dialog */}
      <Dialog
        open={viewAdmitCardOnlyOpen}
        onClose={handleCloseViewAdmitCardOnly}
        maxWidth={false}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: '900px',
            width: '90vw',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t("admit_card_preview")} - {viewAdmitCardOnly?.examType}
          </Typography>
          <IconButton onClick={handleCloseViewAdmitCardOnly} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div ref={admitCardRef}>
            {viewAdmitCardOnly && (
              <AdmitCardTemplate
                data={{
                  ...viewAdmitCardOnly,
                  school: school,
                  session: selectedSession,
                  // Create a mock student object with basic info for template display
                  student: {
                    studentName: "Sample Student",
                    studentCode: "SAMPLE001",
                    enrolmentNo: "ENR001",
                    fatherName: "Sample Father",
                    motherName: "Sample Mother",
                    dateOfBirth: "2000-01-01",
                    gender: "M",
                    className: selectedClass?.name || "Sample Class",
                    section: "A",
                    subjects: ["Math", "Science", "English"],
                    address: "Sample Address",
                    mobileNo: "1234567890",
                    gmail: "sample@email.com"
                  }
                }}
                hidePrintButton={true}
                hideStudentDetails={true}
                showAllSubjects={true}
                hideRollNumberAndPhoto={true}
              />
            )}
          </div>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#3092e3",
                "&:hover": { backgroundColor: "primary.main" }
              }}
              disabled={isGenerating}
              onClick={() => {
                if (admitCardRef.current) {
                  generatePdf(admitCardRef.current, `admit-card-${viewAdmitCardOnly?.examType}-${new Date().toISOString().split('T')[0]}.pdf`);
                }
              }}
            >
              {isGenerating ? "Generating PDF..." : "Generate PDF"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Bulk Download Modal - Only show for non-students */}
      {!isStudent && (
        <Dialog
          open={bulkModalOpen}
          onClose={isBulkGenerating ? undefined : handleCloseBulkModal}
          disableEscapeKeyDown={isBulkGenerating}
          keepMounted
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" fontWeight="bold">
              {t("bulk_download_admit_cards")}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ position: 'relative' }}>
            {/* Progress Section - Only shows when generating */}
            {isBulkGenerating ? (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: "success.light",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "success.main",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                  <Typography variant="body2" color="success.dark" fontWeight="bold">
                    {t("processing_bulk_admit_card_generation")}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="success.dark">
                    {t("progress_complete_cards_processed", {
                      progress,
                      processed: Math.round((progress / 100) * bulkAdmitSelectedStudentIds.size),
                      total: bulkAdmitSelectedStudentIds.size
                    })}
                  </Typography>
                  {currentBulkStudent && (
                    <Typography variant="caption" color="info.dark" sx={{ display: "block", mt: 0.5 }}>
                      {t("currently_processing", { studentName: currentBulkStudent })}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      mt: 1,
                      width: "100%",
                      bgcolor: "rgba(255,255,255,0.5)",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        backgroundColor: "rgba(255,255,255,0.3)",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#4caf50",
                          borderRadius: 1,
                        },
                      }}
                    />
                  </Box>

                  <Typography variant="body2" color="success.dark" sx={{ mt: 1 }}>
                    {t("please_wait_processing_admit_cards", { count: bulkAdmitSelectedStudentIds.size })}
                  </Typography>

                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      bgcolor: "warning.light",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "warning.main",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="warning.dark"
                      fontWeight="bold"
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      {t("important_do_not_reload_processing")}
                    </Typography>
                    <Typography variant="caption" color="warning.dark">
                      {t("reloading_will_interrupt_generation_process")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("select_exam_type_download_admit_cards")}
                </Typography>


                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{t("exam_type")}</InputLabel>
                  <Select
                    value={selectedExamType}
                    label={t("exam_type")}
                    onChange={(e) => setSelectedExamType(e.target.value)}
                    disabled={isBulkGenerating}
                  >
                    {examTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedExamType && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="info.contrastText">
                      {(() => {
                        const activeStudentsCount = bulkAdmitVisibleStudents.length;
                        const className = selectedClass?.name || 'Unknown Class';
                        const sessionName = selectedSession?.name || 'Unknown Session';
                        return t("found_active_students_class_session_exam_type", {
                          count: activeStudentsCount,
                          className,
                          sessionName,
                          examType: selectedExamType
                        });
                      })()}
                    </Typography>
                  </Box>
                )}

                {selectedExamType && bulkAdmitVisibleStudents.length > 0 && (
                  <TableContainer sx={{ maxHeight: 280, mt: 2 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ backgroundColor: "background.paper" }}>
                            <Checkbox
                              checked={bulkAdmitSelectAllChecked}
                              indeterminate={bulkAdmitSelectAllIndeterminate}
                              disabled={isBulkGenerating}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setBulkAdmitSelectedStudentIds(
                                    new Set(
                                      bulkAdmitVisibleStudents.map((s: any) => s.id as number)
                                    )
                                  );
                                } else {
                                  setBulkAdmitSelectedStudentIds(new Set());
                                }
                              }}
                              inputProps={{
                                "aria-label": (t("select_all") as string) || "Select All",
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", backgroundColor: "background.paper" }}>
                            {(t("select_all") as string) || "Select All"} / {t("student_name")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkAdmitVisibleStudents.map((student: any) => (
                          <TableRow key={student.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={bulkAdmitSelectedStudentIds.has(student.id)}
                                disabled={isBulkGenerating}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setBulkAdmitSelectedStudentIds((prev) => {
                                    const next = new Set(prev);
                                    if (checked) next.add(student.id);
                                    else next.delete(student.id);
                                    return next;
                                  });
                                }}
                              />
                            </TableCell>
                            <TableCell>{getStudentDisplayName(student)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseBulkModal} variant="outlined" disabled={isBulkGenerating}>
              {t("cancel")}
            </Button>

            <LoadingButton
              onClick={handleBulkDownload}
              variant="contained"
              // color="#3092e3"

              loading={isBulkGenerating}
              loadingPosition="start"
              loadingIndicator={
                <Typography variant="body2" fontWeight="medium">
                  {t("generating")}...
                </Typography>
              }
              disabled={(() => {
                if (isBulkGenerating) return true;
                if (!selectedExamType || !selectedClass || !selectedSession) return true;

                // Check if admit card exists
                const admitCard = admitCards.find(
                  (card) =>
                    card.examType === selectedExamType &&
                    card.classId === selectedClass.id &&
                    card.sessionId === selectedSession.id
                );

                if (!admitCard) return true;

                // Check if admit card is issued
                const isIssued = admitCard.issued

                if (!isIssued) return true;

                // Check if there are active students
                const activeStudentsCount = bulkAdmitVisibleStudents.length;
                if (activeStudentsCount === 0) return true;
                if (bulkAdmitSelectedStudentIds.size === 0) return true;

                return false;
              })()}
              sx={{
                minWidth: '200px',
              backgroundColor:"#3092e3",
                "&:hover": { backgroundColor: "primary.main" },
                position: 'relative',
                '&.Mui-disabled': {
                  opacity: isBulkGenerating ? 0.8 : 0.6,
                },
              }}
            >
              {(() => {
                const activeStudentsCount = bulkAdmitSelectedStudentIds.size;
                return t("download_admit_cards", { count: activeStudentsCount });
              })()}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default AdmitCardManagement;
